import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Snackbar,
  Grid,
  Paper,
  alpha,
  useTheme
} from '@mui/material';
import { Save, Preview } from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4001';

interface HeroSection {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  buttonText?: string;
  buttonLink?: string;
}

const HeroSectionManagement: React.FC = () => {
  const theme = useTheme();
  const [heroData, setHeroData] = useState<HeroSection>({
    id: '',
    title: 'Value Chain Global',
    subtitle: 'MAKE BETTER INVESTMENT DECISIONS',
    description: 'The go-to platform to discover high-growth Saudi sectors, showcase your offerings, and connect with key industry players. Find your best rewarding place in the Saudi Economic Sectors.',
    buttonText: 'Get Started',
    buttonLink: '/industries'
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    fetchHeroSection();
  }, []);

  const fetchHeroSection = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/hero`);
      if (response.data.data.heroSection) {
        setHeroData(response.data.data.heroSection);
      }
    } catch (error) {
      console.error('Error fetching hero section:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('accessToken');
      await axios.put(`${API_URL}/api/hero`, heroData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setSnackbar({ open: true, message: 'Hero section updated successfully!', severity: 'success' });
      fetchHeroSection();
    } catch (error: any) {
      console.error('Error updating hero section:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error?.message || 'Failed to update hero section',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof HeroSection) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setHeroData({ ...heroData, [field]: e.target.value });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Hero Section Management
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Edit the main hero section that appears on the home page
      </Typography>

      <Grid container spacing={3}>
        {/* Edit Form */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Edit Content
              </Typography>

              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Title"
                  value={heroData.title}
                  onChange={handleChange('title')}
                  margin="normal"
                  required
                  helperText="Main title (e.g., Value Chain Global)"
                />

                <TextField
                  fullWidth
                  label="Subtitle"
                  value={heroData.subtitle}
                  onChange={handleChange('subtitle')}
                  margin="normal"
                  required
                  helperText="Subtitle/tagline (e.g., MAKE BETTER INVESTMENT DECISIONS)"
                />

                <TextField
                  fullWidth
                  label="Description"
                  value={heroData.description}
                  onChange={handleChange('description')}
                  margin="normal"
                  required
                  multiline
                  rows={4}
                  helperText="Main description text"
                />

                <TextField
                  fullWidth
                  label="Button Text (Optional)"
                  value={heroData.buttonText || ''}
                  onChange={handleChange('buttonText')}
                  margin="normal"
                  helperText="CTA button text (leave empty to hide button)"
                />

                <TextField
                  fullWidth
                  label="Button Link (Optional)"
                  value={heroData.buttonLink || ''}
                  onChange={handleChange('buttonLink')}
                  margin="normal"
                  helperText="Button destination URL"
                />

                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<Save />}
                  disabled={loading}
                  fullWidth
                  sx={{ mt: 3 }}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Live Preview */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={3}
            sx={{
              p: 4,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.light, 0.1)} 100%)`,
              border: `2px dashed ${theme.palette.divider}`
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Preview sx={{ mr: 1 }} />
              <Typography variant="h6" fontWeight={600}>
                Live Preview
              </Typography>
            </Box>

            {/* Preview Hero Section */}
            <Paper
              elevation={2}
              sx={{
                p: 4,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                color: 'white',
                minHeight: 300,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}
            >
              <Typography
                variant="h3"
                gutterBottom
                sx={{
                  fontFamily: '"Montserrat", "Open Sauce", sans-serif',
                  fontWeight: 'bold',
                  background: 'linear-gradient(90deg, white, rgba(255,255,255,0.8))',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2
                }}
              >
                {heroData.title}
              </Typography>

              <Typography
                variant="h5"
                gutterBottom
                sx={{
                  mb: 3,
                  fontWeight: 500
                }}
              >
                {heroData.subtitle}
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  opacity: 0.9,
                  mb: 4,
                  fontWeight: 400
                }}
              >
                {heroData.description}
              </Typography>

              {heroData.buttonText && (
                <Box>
                  <Button
                    variant="contained"
                    size="large"
                    sx={{
                      bgcolor: 'white',
                      color: theme.palette.primary.main,
                      '&:hover': {
                        bgcolor: alpha('#ffffff', 0.9)
                      }
                    }}
                  >
                    {heroData.buttonText}
                  </Button>
                </Box>
              )}
            </Paper>
          </Paper>
        </Grid>
      </Grid>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HeroSectionManagement;
